// lib/defaultCurrency.js — what currency to *suggest* when nobody has said one.
//
// Policy (approved 2026-08-25). An account's currency is resolved in this order:
//   1. the explicit currency the institution or the statement reported
//   2. the currency the user already confirmed on the account
//   3. the currency chosen explicitly when the account was created
//   4. otherwise: ask
//
// The profile's base currency and the person's country may PRESELECT or SUGGEST,
// and they must never silently decide. Someone can live in one country, report
// in a second, and hold accounts in a third. A silent default is how an account
// full of Indian statements ended up stored as USD: nobody chose it.
//
// So this module only ever produces a suggestion, and `null` means "you must ask".

export const COUNTRY_CURRENCY = {
  IN: 'INR', India: 'INR',
  AE: 'AED', 'United Arab Emirates': 'AED', UAE: 'AED',
  US: 'USD', 'United States': 'USD',
  GB: 'GBP', UK: 'GBP', 'United Kingdom': 'GBP',
  SG: 'SGD', Singapore: 'SGD',
  AU: 'AUD', Australia: 'AUD',
  CA: 'CAD', Canada: 'CAD',
  SA: 'SAR', 'Saudi Arabia': 'SAR',
  QA: 'QAR', Qatar: 'QAR',
  DE: 'EUR', FR: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', IE: 'EUR',
};

export const currencyForCountry = (country) => {
  if (!country) return null;
  const key = String(country).trim();
  return COUNTRY_CURRENCY[key] || COUNTRY_CURRENCY[key.toUpperCase()] || null;
};

// A suggestion and its provenance, so the UI can say where it came from and the
// person can disagree with it. Never treat this as a decision.
export const suggestCurrency = (profile) => {
  if (profile?.base_currency) {
    return { code: profile.base_currency, from: 'your reporting currency' };
  }
  if (profile?.currency) return { code: profile.currency, from: 'your profile' };
  const byCountry = currencyForCountry(profile?.country);
  if (byCountry) return { code: byCountry, from: `accounts in ${profile.country}` };
  return { code: null, from: null };
};

// Kept for the account form's prefill, which is a visible, editable field the
// person confirms by saving. Returns null when nothing is known.
export const resolveDefaultCurrency = (profile) => suggestCurrency(profile).code;
